import type { ParamValues, PipelineStage, StageGroup } from './types';
import { PIPELINE_STAGES } from './registry';

/**
 * Below the model's actual context window, response quality can start to degrade well before the
 * hard limit is hit (models attend less reliably to content buried in a very long context) — this
 * is a heuristic early-warning threshold, not a published Anthropic figure, hence user-adjustable.
 */
export const DEFAULT_CONTEXT_SOFT_LIMIT_TOKENS = 50_000;

export interface OptimizationSettings {
  /** Group-level master switches. A stage only runs if both its group AND its own toggle are on. */
  groups: Record<StageGroup, boolean>;
  stages: Record<string, boolean>;
  params: Record<string, ParamValues>;
  /** Soft warning threshold (in tokens) for the Context Health indicator — see DEFAULT_CONTEXT_SOFT_LIMIT_TOKENS. */
  contextSoftLimitTokens: number;
}

const ALL_GROUPS: StageGroup[] = ['A', 'B', 'C', 'D', 'E'];

/** The recommended defaults, derived from each stage's own `defaultEnabled`/`params` — one source of truth. */
export function buildRecommendedSettings(stages: PipelineStage[] = PIPELINE_STAGES): OptimizationSettings {
  const groups = Object.fromEntries(ALL_GROUPS.map((g) => [g, true])) as Record<StageGroup, boolean>;
  const stageToggles: Record<string, boolean> = {};
  const params: Record<string, ParamValues> = {};

  for (const stage of stages) {
    stageToggles[stage.id] = stage.defaultEnabled;
    if (stage.params) {
      params[stage.id] = Object.fromEntries(Object.entries(stage.params).map(([k, spec]) => [k, spec.default]));
    }
  }

  return { groups, stages: stageToggles, params, contextSoftLimitTokens: DEFAULT_CONTEXT_SOFT_LIMIT_TOKENS };
}

/** Whether a stage should actually run given current settings (group switch AND stage switch). */
export function isStageEffectivelyEnabled(settings: OptimizationSettings, stage: PipelineStage): boolean {
  const groupOn = settings.groups[stage.group] ?? true;
  const stageOn = settings.stages[stage.id] ?? stage.defaultEnabled;
  return groupOn && stageOn;
}

/** Builds the `enabledStages` map runPipeline() expects, honoring group master switches. */
export function effectiveEnabledMap(
  settings: OptimizationSettings,
  stages: PipelineStage[] = PIPELINE_STAGES,
): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const stage of stages) out[stage.id] = isStageEffectivelyEnabled(settings, stage);
  return out;
}
